import React, { useState, useEffect } from 'react';
import { Popover } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { 
  Ability,
  Character,
  CharacterAbility,
  Reroll,
  RollPlus,
  Armor,
  AbilityEffect,
  Potence,
  CharacterSheetInterface,
  // Adaptation,
  CharacterSpell,
  Attack,
  CharacterItem,
  CharacterWeapon,
  CreatureAbility,
  ExtraEffectsFeature,
  CharacterFeature,
  UpgradableNumber,
  ExtraEffectsOfTypeFeature,
  ExtraEffectTypeFeature,
  CharacterMagicItemKeyword,
  CharacterAction,
  RolledValue,
  CharacterExtraEffects,
  StoredSpell,
} from "@dndModels";
import {
  DAMAGE_TYPES,
  DRACONIC_DAMAGE_TYPES,
  EFFECT_TYPES
} from "@dndModels/Constants";
import { DamageType, EffectType, isDamageType, SpellLevel } from "@dndModels/Types";
// import { SmartHash } from '../../../../../models';
import { makeStyles } from "@utilities/theme_utilities";

import API from "@dndUtilities/system_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import CommonUtilities from "@utilities/common_utilities";

import DandDButtonBox from "@dndComponents/DandDButtonBoxSimple";

import Roller from "@dndComponents/other/Roller";
// import CharacterSummonOptions from '../misc/CharacterSummonOptions';
import DisplayObjects from "@dndComponents/display/DisplayObjects";
import CharacterRollableTableEffect from "@dndComponents/character/spells_and_abilities/effects/CharacterRollableTableEffect";
import CharacterSelfConditionEffect from "@dndComponents/character/spells_and_abilities/effects/CharacterSelfConditionEffect";
// import SelectObjectBox from '../../../../../components/input/selects/SelectObjectBox';
// import LoadingBox from '../../../../../components/input/LoadingBox';
import CharacterCardEffect from "@dndComponents/character/spells_and_abilities/effects/CharacterCardEffect";
import CharacterTransformEffect from "@dndComponents/character/spells_and_abilities/effects/CharacterTransformEffect";
import ImageFromString from "@dndComponents/display/ImageFromString";
import CharacterSelfGiftEffect from "@dndComponents/character/spells_and_abilities/effects/CharacterSelfGiftEffect";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";
import CharacterResourceBoxesFor from "@dndComponents/character/misc/CharacterResourceBoxesFor";
import CharacterSummonEffect from "@dndComponents/character/spells_and_abilities/effects/CharacterSummonEffect";
import CharacterEnhanceItemEffect from "@dndComponents/character/spells_and_abilities/effects/CharacterEnhanceItemEffect";
import CharacterCreateItemEffect from "@dndComponents/character/spells_and_abilities/effects/CharacterCreateItemEffect";
import CharacterEffectStandard from './CharacterEffectStandard';
import CharacterEnhanceCreatureEffect from './CharacterEnhanceCreatureEffect';
import CharacterSpecialFeatureEffect from './CharacterSpecialFeatureEffect';
import { FeatureBase } from '@models';

interface Props {
  character: Character;
  char_interface: CharacterSheetInterface;
  spell?: CharacterAbility | CharacterSpell | StoredSpell;
  action: CharacterAction;
  effect: AbilityEffect;
  cee?: CharacterExtraEffects;
  processing: boolean;
  disabled: boolean;
  log_it?: boolean;
  ee_cf?: CharacterFeature;
  type_cf?: CharacterFeature;
  shared: boolean;
  level: SpellLevel;
  wide: boolean;
  show_extra_effects?: boolean;
  attack_mod?: number;
  main_damage_type?: DamageType;
  casting_time: string; // 'A' | 'BA' | 'RA' | 'Special' | 'Attack' | "1 Minute" | "10 Minutes" | "1 Hour";
  onChange: (change_types: string[]) => void; // For slots, resources, and concentration
  onCostChange: (costs: { [resource: string]: number }) => void;
  // onTransform: () => void;
  hide_touch?: boolean;
}

const DRACONIC_DAMAGE_TYPES_ANY = (DRACONIC_DAMAGE_TYPES as { [key: string]: DamageType });

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const common_util = CommonUtilities.getInstance();

export default function CharacterEffect({
  character,
  char_interface,
  spell,
  action,
  effect,
  cee,
  processing,
  disabled,
  log_it = false,
  ee_cf,
  type_cf,
  shared,
  level,
  wide,
  show_extra_effects = false,
  attack_mod = 0,
  casting_time,
  main_damage_type,
  hide_touch = false,
  onChange,
  onCostChange,
  // onTransform,
}: Props) {
  // const [fetched, setFetched] = useState(false);
  // const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLDivElement | null>(null);
  const [popoverMode, setPopoverMode] = useState("null");
  const [is_processing, setProcessing] = useState(false);
  const [ping, setPing] = useState(false);
  const [roll, setRoll] = useState<RolledValue[]>([]);
  const [extra_details, setExtraDetails] = useState<AbilityEffect | null>(null);

  useEffect(() => {
    if (is_processing !== processing) {
      setProcessing(processing);
    }
    // eslint-disable-next-line
  }, [processing]);
  
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const useStyles = makeStyles()((theme) => {
    return ({
      root: {
        // margin: "4px",
        backgroundColor: theme.palette.secondary.main
      },
    });
  });
  
  const enabled = shared || (!disabled && api.user && (character.owner_id === api.user._id || (character.campaign && character.campaign.owner_id === api.user._id)));
  const item_id = spell instanceof CharacterAbility ? spell.item_id : (spell instanceof CharacterSpell ? spell.source_true_id : "");
  
  const effectType = () => {
    // if (spell instanceof CharacterSpell && spell.name.includes("Vortex")) {
    //   debugger;
    // }
    let effect_type: EffectType | DamageType = effect.type;
    if (effect_type === "Damage" && spell instanceof CharacterSpell) {
      const dt_mods = spell.damage_type_modifications;
      if (dt_mods.length > 0) {
        return dt_mods[0].detail as DamageType;
      }
    }
    if (effect_type === "Selectable Key") {
      const key = effect.selectable_key;
      // let cfs: CharacterFeatureSource | null = null;
      // // console.log(key);
      // // console.log(spell);
      // if (spell && !(spell instanceof StoredSpell)) {
      //   switch (spell.source_type) {
      //     case "Race": cfs = character.race; break;
      //     default:
      //       console.log(spell);
      //       console.log(key);
      //       console.log(effect);
      //       console.log(character);
      //     break;
      //   }
      // }
      // // console.log(cfs);
      // if (cfs) {
      //   // console.log(cfs.selectable_hash[key]);
      //   // console.log(effect.selectable_type);
      //   // console.log(EFFECT_TYPES);
      //   if (cfs.selectable_hash[key]) {
      //     const selected = cfs.selectable_hash[key][0];
      //     if (EFFECT_TYPES.includes(selected)) {
      //       effect_type = selected as EffectType;
      //     } else {
      //       if (effect.selectable_type === "Damage Type" && 
      //         ALL_DRAGONS.includes(selected)) {
      //         effect_type = (DRACONIC_DAMAGE_TYPES as any)[selected];
      //       }
      //     }
      //   }
      // }
      const ancestries = ee_cf ? ee_cf.ancestries : (spell instanceof CharacterAbility ? spell.ancestries : []);
      if (ancestries.length > 0) {
        const source_true_ids = ancestries.map(o => o.true_id);
        let found = false;
        const cfss = character.all_char_feature_sources.filter(o => source_true_ids.includes(o.true_id));
        for (const cfs of cfss) {
          const selectables = cfs.selectable_hash[key];
          if (selectables) {
            for (const dt of selectables) {
              if (isDamageType(dt)) {
                return dt;
              } else {
                // Might be a Dragon Breed, with an associated Damage Type
                const draconic_dt = DRACONIC_DAMAGE_TYPES_ANY[dt];
                if (draconic_dt) {
                  return draconic_dt;
                }
              }
            }
            found = true;
            break;
          }
        }
        if (!found) {
          // It could be a feature from a main cfs that has the selectable set in a special_feature
          const cfs = character.all_char_feature_sources.find(o => o.source_type === "SpecialFeature" && o.selectable_hash[key] !== undefined);
          if (cfs) {
            const selectables = cfs.selectable_hash[key];
            if (selectables) {
              for (const dt of selectables) {
                if (isDamageType(dt)) {
                  return dt as DamageType;
                } else {
                  // Might be a Dragon Breed, with an associated Damage Type
                  const draconic_dt = DRACONIC_DAMAGE_TYPES_ANY[dt];
                  if (draconic_dt) {
                    return draconic_dt;
                  }
                }
              }
            }
          }
        }
      }
    } else if (effect_type === "Use Extra") {
      if (ee_cf) {
        const char_item = character.all_char_feature_sources.find(o => o.true_id === ee_cf.item_id);
        const cmik = character.all_char_feature_sources.find(o => o.true_id === ee_cf.source_true_id);
        if (char_item instanceof CharacterItem && cmik instanceof CharacterMagicItemKeyword) {
          if (cmik.source) {
            if (cmik.source.extra_base_type === "Dragon Breed") {
              effect_type = (DRACONIC_DAMAGE_TYPES as any)[char_item.extra];
            } else if (EFFECT_TYPES.includes(char_item.extra)) {
              effect_type = char_item.extra as EffectType;
            }
          }
        }
      } else if (spell instanceof CharacterAbility) {
        const ancestries = spell.ancestries.map(o => o.true_id);
        const cfss = character.all_char_feature_sources.filter(o => 
          ancestries.includes(o.true_id) && 
          o.char_feature_bases.filter(o2 => 
            o2.features.filter(o3 => o3.floating_feature instanceof Ability && 
              o3.floating_feature.effect_types.includes("Use Extra")).length > 0
          ).length > 0
        );
        if (cfss.length === 1) {
          const cfs = cfss[0];
          if (cfs instanceof CharacterMagicItemKeyword && cfs.source) {
            const char_item = character.all_char_feature_sources.find(o => o.true_id === item_id);
            if (char_item instanceof CharacterItem) {
              if (cfs.source.extra_base_type === "Dragon Breed") {
                effect_type = (DRACONIC_DAMAGE_TYPES as any)[char_item.extra];
              } else if (EFFECT_TYPES.includes(char_item.extra)) {
                effect_type = char_item.extra as EffectType;
              }
            }
          } else {
            console.error(cfss);
            console.error(spell);
            console.error(cfs);
          }
        } else {
          console.error(cfss);
          console.error(spell);
        }
      }
    }
    if (effect_type === "Same as Main" && main_damage_type && main_damage_type !== "Typeless" && main_damage_type !== "Same as Main") {
      effect_type = main_damage_type;
    }
    return effect_type;
  }

  const { classes, cx } = useStyles();
  const renderExtras = () => {
    if (extra_details) {
      console.log(extra_details);
    }
    // Make it so if the CharacterExtraEffects uses a resource, give <CheckBoxes /> here for it.
    // Also put the effect row here.
    return (
      <>
        <Popover
          open={ popoverAnchorEl !== null && popoverMode !== "Cast" }
          anchorEl={popoverAnchorEl}
          onClose={() => {
            setPopoverAnchorEl(null);
            setPopoverMode("");
            // setKeepRolls([]);
          }}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          { renderRoll() }
        </Popover>
        <ModalOrDrawer
          open={extra_details !== null}
          onClose={() => { 
            setExtraDetails(null); 
            character.subscription_holder.modalClosed();
          }}
        >
          <>
            { renderExtraDetails() }
          </>
        </ModalOrDrawer>
      </>
    );
  }

  const renderExtraDetails = () => {
    if (extra_details) {
      // const potence = effect.potences.length > 0 ? effect.potences[0] : null;
      
      const effect_type = effectType();
      let potence = ee_cf && (ee_cf.floating_feature instanceof ExtraEffectsFeature || ee_cf.floating_feature instanceof ExtraEffectsOfTypeFeature) ? 
        char_util.getPotence({ slot_level: level, char: character, char_int: char_interface, spell: ee_cf, item_id, effect, cfs_id: (ee_cf ? ee_cf.source_id : ""), attack_mod }) : 
        spell ? char_util.getPotence({ slot_level: level, char: character, char_int: char_interface, spell, effect, item_id, cfs_id: "", attack_mod, action }) : null;
      let extra_string = "";
      if (potence) {
        extra_string = potence.extra;
      }
      // if (ee_cf && ee_cf.name.includes("Graze")) {
      //   debugger;
      //   log_it = true;
      // }
      let potence_string = ee_cf && (ee_cf.floating_feature instanceof ExtraEffectsFeature || ee_cf.floating_feature instanceof ExtraEffectsOfTypeFeature) ? 
        char_util.getPotenceString({slot_level: level, char: character, char_int: char_interface, item_id, spell: ee_cf, effect, cfs_id: (ee_cf ? ee_cf.source_true_id : ""), attack_mod }) 
        : spell ? 
        char_util.getPotenceString({slot_level: level, char: character, char_int: char_interface, item_id, spell, effect, cfs_id: spell instanceof StoredSpell ? spell.item_id : spell.source_true_id, attack_mod, action }) : "";
      // console.log(potence);
      if (log_it) {
        console.log(effect);
        console.log(potence);
        console.log(potence_string);
      }
      if (character.character_options.potion_max_on_action && 
        spell instanceof CharacterAbility && 
        spell.casting_times.includes("A") && 
        spell.char_feature && 
        spell.char_feature.feature && 
        effect.type === "Healing" && 
        spell.source_name.includes("Healing") && potence) {
        const max = char_util.maxRoll(potence.rolls, { obj: character, source_id: spell.source_true_id });
        const new_potence = potence.clone();
        new_potence.rolls.size = new UpgradableNumber(0);
        new_potence.rolls.count = new UpgradableNumber(0);
        new_potence.rolls.flat = new UpgradableNumber(max);
        potence = new_potence;
        potence_string = `${max}`; 
      }
      // if (spell && spell.name.includes("Daylight")) {
      //   debugger;
      // }
      // if (ee_cf && ee_cf.name.includes("Suffocate")) {
      //   debugger;
      // }
      // if (effect.name.includes("Suffocate")) {
      //   debugger;
      // }
      const { advantage, advantages, greater_advantage } = char_util.getAdvantage(character, "Attack", spell);
      let range_string = char_util.getRangeString({ char_int: char_interface, spell, effect }); // , log_it: spell && spell.name.includes("Daylight")
      const attack = char_util.getAttack({ char_int: char_interface, spell, effect, action });
      let spell_dc = char_util.getDC({ char_int: char_interface, spell, effect, action });
      if (ee_cf) {
        // if (ee_cf.name.includes("Sneak")) {
        //   debugger;
        // }
        if (ee_cf.floating_feature instanceof ExtraEffectsFeature) {
          if (!ee_cf.floating_feature.show_range) {
            range_string = "";
          }
          if (!ee_cf.floating_feature.attack_bonus.isBlank()) {
            const attack_bonus = char_util.value(ee_cf.floating_feature.attack_bonus, { obj: char_interface, source_id: ee_cf.source_true_id, action });
            console.log(attack);
            console.log(attack_bonus);
          }
          if (!ee_cf.floating_feature.save_dc.isBlank()) {
            spell_dc = char_util.value(ee_cf.floating_feature.save_dc, { obj: char_interface, source_id: ee_cf.source_true_id, action });
          }
          // if (ee_cf.floating_feature.saving_throw_ability_score !== "") {
          //   saving_throw_ability_score = ee_cf.floating_feature.saving_throw_ability_score;
          // }
        } else if (ee_cf.floating_feature instanceof ExtraEffectsOfTypeFeature) {
          // Get the type's feature.
          if (type_cf && type_cf.floating_feature instanceof ExtraEffectTypeFeature) {
            if (!type_cf.floating_feature.show_range) {
              range_string = "";
            }
            if (!type_cf.floating_feature.attack_bonus.isBlank()) {
              const attack_bonus = char_util.value(type_cf.floating_feature.attack_bonus, { obj: char_interface, source_id: ee_cf.source_true_id, action });
              console.log(attack);
              console.log(attack_bonus);
            }
            if (!type_cf.floating_feature.save_dc.isBlank()) {
              spell_dc = char_util.value(type_cf.floating_feature.save_dc, { obj: char_interface, source_id: ee_cf.source_true_id, action });
            }
          }
        }
        if (range_string.length > 0) {
          range_string += "\r";
        }
        range_string += effect.name === "" ? ee_cf.name : effect.name;
      }
      let damage_types = common_util.unique_array(effect.damage_types);
      if (damage_types.includes("Same as Main") && main_damage_type) {
        damage_types = damage_types.filter(o => o !== "Same as Main");
        damage_types.push(main_damage_type);
      }
      let damage_type_options = action.damage_type_options;
      if (action.level !== level) {
        if (action.spell) {
          // This is the wrong level for the damage types on the action.
          // Look for another option.
          const spell_id = action.spell._id;
          const action_finder = character.actions.find(o => o.level === level && o.spell && o.spell._id === spell_id);
          if (action_finder) {
            damage_type_options = action_finder.damage_type_options;
          }
        }
      }
      // console.log(cee);
      if (hide_touch && (range_string === "Touch" || range_string === "Self")) {
        range_string = "";
      }
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            container
            spacing={0}
            direction="row"
            size={{
              xs: 12
            }}>
            <Grid
              className="header centered"
              size={{
                xs: 12
              }}>{ extra_details.name === "" && ee_cf ? ee_cf.name : extra_details.name }</Grid>
            <Grid
              size={{
                xs: 12
              }}>
              <DisplayStringWithReplacements display_string={ extra_details.description === "" && ee_cf ? ee_cf.description : extra_details.description } />
            </Grid>
            { cee &&
              <>
                <Grid
                  className="padded"
                  size={{
                    xs: 12
                  }}>
                  <Grid container spacing={0} direction="row" className={cx(classes.root, "centered")}>
                    <Grid
                      size={{
                        xs: 3
                      }}>
                      { ["","None"].includes(cee.resource_consumed) ? 
                        <Grid container spacing={0} direction="row">
                          { cee.name !== "" && 
                            <Grid
                              size={{
                                xs: 12
                              }}>
                              { cee.name }
                            </Grid>
                          }
                          { cee.time_restriction !== "Hide" && 
                            <Grid
                              size={{
                                xs: 12
                              }}>
                              { (cee.time_restriction === "Special" ? "" : cee.time_restriction) + (cee.extra_restriction === "" ? "" : ` ${cee.extra_restriction}`) }
                            </Grid> 
                          }
                        </Grid>
                      : renderResourceCheckboxes() }
                    </Grid>
                    <Grid
                      className="centered small_label"
                      size={{
                        xs: 3
                      }}>
                      { range_string }{ extra_string !== "" ? (range_string !== "" ? ` / ${extra_string}` : extra_string) : "" }
                    </Grid>
                    <Grid
                      className="centered small_label"
                      size={{
                        xs: 3
                      }}>
                      { effect.use_attack ?
                        <DandDButtonBox
                          name={ attack.attack_string }
                          advantage={ advantage }
                          advantages={ advantages }
                          greater_advantage={ greater_advantage }
                          disabled={ !enabled }
                          // minimum_roll={minimum_roll}
                          onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                            if (potence_string === "Apply") {
                              // const self_condition_ids = effect.conditions_applied;
                              // if (self_condition_ids.length > 0) {
                              //   // Apply the conditions
                              //   self_condition_ids.forEach(cond_id => {
                              //     if (!character.condition_hash[cond_id]) {
                              //       character.condition_hash[cond_id] = 1;
                              //     }
                              //   });
                              //   setPing(!ping);
                              //   onChange(["Condition"]);
                              //   // updateCharacter(["Condition"]);
                              // }
                            } else {
                              doRoll("Attack");
                              setPopoverMode("Attack");
                              setPopoverAnchorEl(event.currentTarget);
                            }
                          }} 
                        />
                      : effect.attack_type === "Save" &&
                        <span>
                          { `${effect.saving_throw_ability_score} ${spell_dc}` }
                        </span> 
                      }
                    </Grid>
                    <Grid
                      className="centered small_label"
                      size={{
                        xs: 3
                      }}>
                      { effect_type === "Damage" ? 
                        <>
                          { damage_type_options.length > 0 ? 
                            <>
                              { damage_type_options.length < 2 ?
                                <DandDButtonBox
                                  name={ potence_string }
                                  style={{ fontSize: "9" }}                          
                                  image={ damage_type_options.length > 0 ? damage_type_options[0] : "" }
                                  disabled={ !enabled }
                                  onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                                    if (potence_string === "Apply") {
                                      // const self_condition_ids = effect.conditions_applied;
                                      // if (self_condition_ids.length > 0) {
                                      //   // Apply the conditions
                                      //   // const character = char;
                                      //   // self_condition_ids.forEach(cond_id => {
                                      //   //   character.conditions.push(cond_id);
                                      //   // });
                                      //   setPing(!ping);
                                      //   onChange([]);
                                      //   // updateCharacter(["Condition"]);
                                      // }
                                    } else {
                                      doRoll("Damage");
                                      setPopoverMode("Damage");
                                      setPopoverAnchorEl(event.currentTarget);
                                    }
                                  }} 
                                />
                              : damage_type_options.length > 1 &&
                                <Grid container spacing={0} direction="row">
                                  <Grid
                                    size={{
                                      xs: 12
                                    }}>
                                    <DandDButtonBox
                                      name={ potence_string }
                                      style={{ fontSize: "9" }}                          
                                      image={ damage_type_options[0] }
                                      disabled={ !enabled }
                                      onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                                        if (potence_string === "Apply") {
                                          // const self_condition_ids = effect.conditions_applied;
                                          // if (self_condition_ids.length > 0) {
                                          //   // Apply the conditions
                                          //   // const character = char;
                                          //   // self_condition_ids.forEach(cond_id => {
                                          //   //   character.conditions.push(cond_id);
                                          //   // });
                                          //   setPing(!ping);
                                          //   onChange([]);
                                          //   // updateCharacter(["Condition"]);
                                          // }
                                        } else {
                                          doRoll("Damage");
                                          setPopoverMode("Damage");
                                          setPopoverAnchorEl(event.currentTarget);
                                        }
                                      }} 
                                    />
                                  </Grid>
                                  <Grid
                                    size={{
                                      xs: 12
                                    }}>
                                    { damage_type_options.map((dt, key) => {
                                      return <span key={key}><ImageFromString image={dt} />&nbsp;</span>;
                                    })}
                                  </Grid>
                                </Grid>
                              }
                            </>
                          : damage_types.length < 2 ?
                            <DandDButtonBox
                              name={ potence_string }
                              style={{ fontSize: "9" }}                          
                              image={ damage_types.length > 0 ? damage_types[0] : "" }
                              disabled={ !enabled }
                              onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                                if (potence_string === "Apply") {
                                  // const self_condition_ids = effect.conditions_applied;
                                  // if (self_condition_ids.length > 0) {
                                  //   // Apply the conditions
                                  //   // const character = char;
                                  //   // self_condition_ids.forEach(cond_id => {
                                  //   //   character.conditions.push(cond_id);
                                  //   // });
                                  //   setPing(!ping);
                                  //   onChange([]);
                                  //   // updateCharacter(["Condition"]);
                                  // }
                                } else {
                                  doRoll("Damage");
                                  setPopoverMode("Damage");
                                  setPopoverAnchorEl(event.currentTarget);
                                }
                              }} 
                            />
                          : damage_types.length > 1 &&
                            <Grid container spacing={0} direction="row">
                              <Grid
                                size={{
                                  xs: 12
                                }}>
                                <DandDButtonBox
                                  name={ potence_string }
                                  style={{ fontSize: "9" }}                          
                                  image={ damage_types[0] }
                                  disabled={ !enabled }
                                  onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                                    if (potence_string === "Apply") {
                                      // const self_condition_ids = effect.conditions_applied;
                                      // if (self_condition_ids.length > 0) {
                                      //   // Apply the conditions
                                      //   // const character = char;
                                      //   // self_condition_ids.forEach(cond_id => {
                                      //   //   character.conditions.push(cond_id);
                                      //   // });
                                      //   setPing(!ping);
                                      //   onChange([]);
                                      //   // updateCharacter(["Condition"]);
                                      // }
                                    } else {
                                      doRoll("Damage");
                                      setPopoverMode("Damage");
                                      setPopoverAnchorEl(event.currentTarget);
                                    }
                                  }} 
                                />
                              </Grid>
                              <Grid
                                size={{
                                  xs: 12
                                }}>
                                { damage_types.map((dt, key) => {
                                  return <span key={key}><ImageFromString image={dt} />&nbsp;</span>;
                                })}
                              </Grid>
                            </Grid>
                          }
                        </> 
                      : !["Control","Utility","Teleport","Portal","Summon","Transform","Create Resource","None","Special"].includes(effect_type) ?
                        <DandDButtonBox
                          name={ potence_string }
                          style={{ fontSize: "9" }}                          
                          image={ effect_type }
                          disabled={ !enabled }
                          onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                            if (potence_string === "Apply") {
                              // const self_condition_ids = effect.conditions_applied;
                              // if (self_condition_ids.length > 0) {
                              //   // Apply the conditions
                              //   // const character = char;
                              //   // self_condition_ids.forEach(cond_id => {
                              //   //   character.conditions.push(cond_id);
                              //   // });
                              //   setPing(!ping);
                              //   onChange([]);
                              //   // updateCharacter(["Condition"]);
                              // }
                            } else {
                              doRoll("Damage");
                              setPopoverMode("Damage");
                              setPopoverAnchorEl(event.currentTarget);
                            }
                          }} 
                        />
                      : ["Control","Utility","Teleport","Portal"].includes(effect_type) && effect.utility_detail !== "" ?
                        <span>
                          { effect.utility_detail }
                        </span> 
                      :
                        <span>
                          { effect_type }
                        </span> 
                      }
                    </Grid>
                  </Grid>
                </Grid>
              </>
            }
          </Grid>
        </Grid>
      );
    }
  }

  const renderResourceCheckboxes = () => {
    if (cee && !["","None"].includes(cee.resource_consumed)) {
      return (
        <Grid
          size={{
            xs: 12
          }}>
          <CharacterResourceBoxesFor
            char_interface={char_interface}
            character={character}
            obj={cee}
            shared={shared}
            onChange={async () => {
              await char_util.resourcesChanged(character);
              setPing(!ping);
            }}
          />
        </Grid>
      );
    }
    return null;
  }

  const doRoll = (mode: string) => {
    const ability = spell;
    const { advantage, greater_advantage } = char_util.getAdvantage(character, "Attack", spell);
    // debugger;
    if (ability && effect) {
      if (ability instanceof CharacterItem) {
        if (ability instanceof CharacterWeapon) {
          
        } else {

        }
      } else if (ability instanceof Attack) {
        
      } else {
        const effect_type = effectType();
        if (ability.the_spell) {
          if (mode === "Attack") {
            if (ability.attack.attack_rolls.length === 0) {
              const rolls = new RollPlus();
              if (ability instanceof CharacterAbility) {
                rolls.flat = ability.spell_attack;
              } else if (!(ability instanceof StoredSpell)) {
                const source_id = ability.source_id;
                const source = character.classes.find(o => o.source_id === source_id);
                if (source) {
                  rolls.flat = source.spell_attack;
                }
              }
              ability.attack.attack_rolls.push(rolls);
            }
            const minimum_roll = char_util.getMinimumRoll(character, "Attack", ability.attack);
            // const greater_advantage = character.greater_advantages.filter(o => (o.roll_types.length === 0 || o.roll_types.includes("Attack")) && ability && ability.attack.attack_stat !== "None" && (o.use_ability_scores.length === 0 || o.use_ability_scores.includes(ability.attack.attack_stat))).length > 0;
            setRoll(char_util.rollD20({ char_interface: character, rolls: ability.attack.attack_rolls, use_ability_score: ability.attack.attack_stat, type: "Attack", advantage, greater_advantage, minimum_roll }));
          } else if (mode === "Damage") {
            const potence = char_util.getPotence({ slot_level: level, char: character, char_int: char_interface, item_id, spell: (ee_cf && (ee_cf.floating_feature instanceof ExtraEffectsFeature || ee_cf.floating_feature instanceof ExtraEffectsOfTypeFeature) ? ee_cf : ability), effect, cfs_id: (ee_cf ? ee_cf.source_id : ""), attack_mod, action });
            let damage_rolls: RollPlus[] = [];
            if (potence) {
              const roll_plus = char_util.toRollPlus(potence.rolls, { 
                obj: character,
                source_id: ability.source_true_id, 
                base_slot_level: ability.level, 
                slot_level: level, 
              });
              damage_rolls.push(roll_plus);
            }
            damage_rolls = [...damage_rolls,...ability.attack.damage_rolls];
            
            const rerolls: Reroll[] = [];
            for (const r of character.rerolls) {
              let good = false;
              if (r.allowed_armor_types.includes("ALL")) {
                // Check for required
                if (r.required_armor_types.includes("None")) {
                  good = true;
                } else if (r.required_armor_types.includes("Any")) {
                  good = character.equipped_items.filter(o => 
                    o.base_item &&
                    o.base_item.item_type === "Armor" &&
                    o.base_item.name !== "Shield"
                  ).length > 0;
                } else {
                  const missing_armor_types = r.required_armor_types.filter(a =>
                    character.equipped_items.filter(o =>
                      o.base_item &&
                      o.base_item.item_type === "Armor" &&
                      o.base_item instanceof Armor &&
                      o.base_item.armor_type_id === a).length === 0);
                  good = missing_armor_types.length === 0;
                }
              } else if (r.allowed_armor_types.includes("None")) {
                good = character.equipped_items.filter(o => 
                  o.base_item &&
                  o.base_item.item_type === "Armor" &&
                  o.base_item.name !== "Shield"
                ).length === 0;
              } else {
                const bad_armor_items = character.equipped_items.filter(o =>
                  o.base_item &&
                  o.base_item.item_type === "Armor" &&
                  o.base_item instanceof Armor &&
                  !r.allowed_armor_types.includes(o.base_item.armor_type_id));
      
                good = bad_armor_items.length === 0;
              }
              if (good) {
                if (!r.required_weapon_keywords.includes("None")) {
                } else if (!r.allowed_damage_types.includes("ALL")) {
                  good = false;
                  for (let i = 0; i < damage_rolls.length; ++i) {
                    if (r.allowed_damage_types.includes(damage_rolls[i].type)) {
                      good = true;
                      break;
                    }
                  }
                }
                rerolls.push(r);
              }
            }
            const minimum_roll = char_util.getMinimumRoll(char_interface, effect_type, effect, ability);
            if (effect_type === "Healing" || effect_type === "Temp HP" || effect_type === "Max HP") {
              setRoll(char_util.rollOther({ char_interface, rolls: damage_rolls, minimum_roll, type: effect_type, rerolls }));
            } else {
              setRoll(char_util.rollOther({ char_interface, rolls: damage_rolls, minimum_roll, type: "Damage", rerolls }));
            }
          } else if (mode === "Create") {
            console.log("Not Implemented");
          }
        } else if (ability instanceof CharacterSpell || ability instanceof StoredSpell) {

        } else if (ability.the_ability instanceof Ability || 
          ability.the_ability instanceof CreatureAbility) {
          if (mode === "Attack") {
            // console.log(ability);
            if (ability.attack.attack_rolls.length === 0) {
              const rolls = new RollPlus();
              rolls.flat = ability.spell_attack;
              ability.attack.attack_rolls.push(rolls);
            }
            const use_ability_score = ability.attack.attack_stat;
            const minimum_roll = char_util.getMinimumRoll(character, "Attack", ability.attack);
            const greater_advantage = character.greater_advantages.filter(o => (o.roll_types.length === 0 || o.roll_types.includes("Attack")) && use_ability_score !== "None" && (o.use_ability_scores.length === 0 || o.use_ability_scores.includes(use_ability_score))).length > 0;
            setRoll(char_util.rollD20({ char_interface: character, rolls: ability.attack.attack_rolls, type: "Attack", use_ability_score, advantage, greater_advantage, minimum_roll }));
          } else if (mode === "Damage") {
            let damage_rolls: RollPlus[] = [];
            let potence = char_util.getPotence({ slot_level: level, char: character, char_int: char_interface, item_id, spell: (ee_cf && (ee_cf.floating_feature instanceof ExtraEffectsFeature || ee_cf.floating_feature instanceof ExtraEffectsOfTypeFeature) ? ee_cf : ability), effect, cfs_id: (ee_cf ? ee_cf.source_id : ""), attack_mod, action });
            if (character.character_options.potion_max_on_action && 
              spell instanceof CharacterAbility && 
              spell.casting_times.includes("A") && 
              spell.char_feature && 
              spell.char_feature.feature && 
              effect.type === "Healing" && 
              spell.source_name.includes("Healing") && potence) {
              const max = char_util.maxRoll(potence.rolls, { obj: character, source_id: spell.source_true_id });
              const new_potence = potence.clone();
              new_potence.rolls.size = new UpgradableNumber(0);
              new_potence.rolls.count = new UpgradableNumber(0);
              new_potence.rolls.flat = new UpgradableNumber(max);
              potence = new_potence;
            }
            if (potence) {
              const roll_plus = char_util.toRollPlus(potence.rolls, { 
                obj: character,
                source_id: ability.source_true_id, 
                base_slot_level: ability.level, 
                slot_level: level, 
              });
              damage_rolls.push(roll_plus);
            }
            damage_rolls = [...damage_rolls,...ability.attack.damage_rolls];
            
            const rerolls: Reroll[] = [];
            for (const r of character.rerolls) {
              let good = false;
              if (r.allowed_armor_types.includes("ALL")) {
                // Check for required
                if (r.required_armor_types.includes("None")) {
                  good = true;
                } else if (r.required_armor_types.includes("Any")) {
                  good = character.equipped_items.filter(o => 
                    o.base_item &&
                    o.base_item.item_type === "Armor" &&
                    o.base_item.name !== "Shield"
                  ).length > 0;
                } else {
                  const missing_armor_types = r.required_armor_types.filter(a =>
                    character.equipped_items.filter(o =>
                      o.base_item &&
                      o.base_item.item_type === "Armor" &&
                      o.base_item instanceof Armor &&
                      o.base_item.armor_type_id === a).length === 0);
                  good = missing_armor_types.length === 0;
                }
              } else if (r.allowed_armor_types.includes("None")) {
                good = character.equipped_items.filter(o => 
                  o.base_item &&
                  o.base_item.item_type === "Armor" &&
                  o.base_item.name !== "Shield"
                ).length === 0;
              } else {
                const bad_armor_items = character.equipped_items.filter(o =>
                  o.base_item &&
                  o.base_item.item_type === "Armor" &&
                  o.base_item instanceof Armor &&
                  !r.allowed_armor_types.includes(o.base_item.armor_type_id));
      
                good = bad_armor_items.length === 0;
              }
              if (good) {
                if (!r.required_weapon_keywords.includes("None")) {
                } else if (!r.allowed_damage_types.includes("ALL")) {
                  good = false;
                  for (let i = 0; i < damage_rolls.length; ++i) {
                    if (r.allowed_damage_types.includes(damage_rolls[i].type)) {
                      good = true;
                      break;
                    }
                  }
                }
                rerolls.push(r);
              }
            }
            if (effect_type === "Healing" || effect_type === "Temp HP" || effect_type === "Max HP") {
              setRoll(char_util.rollOther({ char_interface: character, rolls: damage_rolls, type: effect_type, rerolls }));
            } else {
              setRoll(char_util.rollOther({ char_interface: character, rolls: damage_rolls, type: "Damage", rerolls }));
            }
          } else if (mode === "Create") {
            const potence = char_util.getPotence({ slot_level: level, char: character, char_int: char_interface, item_id, spell: (ee_cf && (ee_cf.floating_feature instanceof ExtraEffectsFeature || ee_cf.floating_feature instanceof ExtraEffectsOfTypeFeature) ? ee_cf : ability), effect, cfs_id: (ee_cf ? ee_cf.source_id : ""), attack_mod, action });
            const damage_rolls: RollPlus[] = [];
            if (potence) {
              const roll_plus = char_util.toRollPlus(potence.create_resource.amount, { 
                obj: character,
                source_id: ability.source_true_id, 
                base_slot_level: ability.level, 
                slot_level: level, 
              });
              damage_rolls.push(roll_plus);
              // const up_to_max = char_util.value(potence.create_resource.up_to_max, { obj: character, source_id: ability.source_true_id, root_id: ability.source_true_id });
    
              setRoll(char_util.rollOther({ char_interface: character, rolls: damage_rolls, type: effect_type }));
              // setMax(up_to_max);
            }
            return null;
          } else {
            console.error("Not Implemented");
            debugger;
          }
        }
      }
    } else if (effect && ee_cf && 
      (ee_cf.floating_feature instanceof ExtraEffectsFeature || ee_cf.floating_feature instanceof ExtraEffectsOfTypeFeature)) {
      // const ff = ee_cf.floating_feature;
      const effect_type = effectType();
      if (mode === "Attack") {
      } else if (mode === "Damage") {
        const damage_rolls: RollPlus[] = [];
        let potence = char_util.getPotence({ slot_level: level, char: character, char_int: char_interface, item_id, spell: ee_cf, effect, cfs_id: ee_cf.source_id, attack_mod, action });
        if (character.character_options.potion_max_on_action && 
          spell instanceof CharacterAbility && 
          spell.casting_times.includes("A") && spell.char_feature && 
          spell.char_feature.feature && 
          effect.type === "Healing" && 
          spell.source_name.includes("Healing") && potence) {
          const max = char_util.maxRoll(potence.rolls, { obj: character, source_id: spell.source_true_id });
          const new_potence = potence.clone();
          new_potence.rolls.size = new UpgradableNumber(0);
          new_potence.rolls.count = new UpgradableNumber(0);
          new_potence.rolls.flat = new UpgradableNumber(max);
          potence = new_potence;
        }
        if (potence) {
          const roll_plus = char_util.toRollPlus(potence.rolls, { 
            obj: character,
            source_id: ee_cf.source_true_id, 
            base_slot_level: 0, 
            slot_level: level, 
          });
          if (action) {
            let damage_type_options = action.damage_type_options;
            if (action.level !== level) {
              if (action.spell) {
                // This is the wrong level for the damage types on the action.
                // Look for another option.
                const spell_id = action.spell._id;
                const action_finder = character.actions.find(o => o.level === level && o.spell && o.spell._id === spell_id);
                if (action_finder) {
                  damage_type_options = action_finder.damage_type_options;
                }
              }
            }
            if (effect_type === "Damage" && damage_type_options.length === 1) {
              roll_plus.type = damage_type_options[0];
            } else {
              roll_plus.type = effect_type;
            }
          } else {
            if (effect_type === "Damage" && effect && effect.damage_types.length === 1) {
              roll_plus.type = effect.damage_types[0];
            } else {
              roll_plus.type = effect_type;
            }
          }
          damage_rolls.push(roll_plus);
        }
        // damage_rolls = [...damage_rolls,...ability.attack.damage_rolls];
        
        const rerolls: Reroll[] = [];
        for (const r of character.rerolls) {
          let good = false;
          if (r.allowed_armor_types.includes("ALL")) {
            // Check for required
            if (r.required_armor_types.includes("None")) {
              good = true;
            } else if (r.required_armor_types.includes("Any")) {
              good = character.equipped_items.filter(o => 
                o.base_item &&
                o.base_item.item_type === "Armor" &&
                o.base_item.name !== "Shield"
              ).length > 0;
            } else {
              const missing_armor_types = r.required_armor_types.filter(a =>
                character.equipped_items.filter(o =>
                  o.base_item &&
                  o.base_item.item_type === "Armor" &&
                  o.base_item instanceof Armor &&
                  o.base_item.armor_type_id === a).length === 0);
              good = missing_armor_types.length === 0;
            }
          } else if (r.allowed_armor_types.includes("None")) {
            good = character.equipped_items.filter(o => 
              o.base_item &&
              o.base_item.item_type === "Armor" &&
              o.base_item.name !== "Shield"
            ).length === 0;
          } else {
            const bad_armor_items = character.equipped_items.filter(o =>
              o.base_item &&
              o.base_item.item_type === "Armor" &&
              o.base_item instanceof Armor &&
              !r.allowed_armor_types.includes(o.base_item.armor_type_id));

            good = bad_armor_items.length === 0;
          }
          if (good) {
            if (!r.required_weapon_keywords.includes("None")) {
            } else if (!r.allowed_damage_types.includes("ALL")) {
              good = false;
              for (let i = 0; i < damage_rolls.length; ++i) {
                if (r.allowed_damage_types.includes(damage_rolls[i].type)) {
                  good = true;
                  break;
                }
              }
            }
            rerolls.push(r);
          }
        }
        if (effect_type === "Healing" || effect_type === "Temp HP" || effect_type === "Max HP") {
          setRoll(char_util.rollOther({ char_interface: character, rolls: damage_rolls, type: effect_type, rerolls }));
        } else {
          setRoll(char_util.rollOther({ char_interface: character, rolls: damage_rolls, type: "Damage", rerolls }));
        }
      } else if (mode === "Create") {
        const potence = char_util.getPotence({ slot_level: level, char: character, char_int: char_interface, item_id, spell: ee_cf, effect, cfs_id: (ee_cf ? ee_cf.source_id : ""), attack_mod, action });
        const damage_rolls: RollPlus[] = [];
        if (potence) {
          const roll_plus = char_util.toRollPlus(potence.create_resource.amount, { 
            obj: character,
            source_id: ee_cf.source_true_id, 
            base_slot_level: -1, 
            slot_level: level, 
          });
          damage_rolls.push(roll_plus);
          // const up_to_max = char_util.value(potence.create_resource.up_to_max, { obj: character, source_id: ee_cf.source_true_id, root_id: ee_cf.source_true_id });
          setRoll(char_util.rollOther({ char_interface: character, rolls: damage_rolls, type: effect_type }));
          // setMax(up_to_max);
        }
        return null;
      } else {
        console.error("Not Implemented");
        debugger;
      }
    }
  }

  const renderCreateResourceControls = (potence: Potence) => {
    const amount = potence.create_resource.amount;
    if (spell && !(spell instanceof CharacterItem || spell instanceof Attack)) {
      // if (spell.name.includes("Absorption")) {
      //   debugger;
      // }
      const effect_type = effectType();
      const flat = char_util.value(amount.flat, { obj: character, source_id: spell.source_true_id, root_id: spell.source_true_id, action });
      const count = char_util.value(amount.count, { obj: character, source_id: spell.source_true_id, root_id: spell.source_true_id, action });
      const size = char_util.value(amount.size, { obj: character, source_id: spell.source_true_id, root_id: spell.source_true_id, action });
      const up_to_max = char_util.value(potence.create_resource.up_to_max, { obj: character, source_id: spell.source_true_id, root_id: spell.source_true_id, action });
      if (size !== null && count !== null && flat !== null) {
        let str = effect.refresh_only ? "Refresh " : "Create ";
        if (size > 1 && count > 0) {
          str += `${count}d${size}` + (flat === 0 ? "" : `+${flat}`);
          let maxed = false;
          if (up_to_max > 0) {
            str += ` up to ${up_to_max}`;
            if (potence.create_resource.resource_type === "Slot") {
              // I'm never going to use it for Slots, or am I?
              debugger;
            } else {
              const resource_finder = character.resources.filter(o => o.type_id === potence.create_resource.resource_type);
              if (resource_finder.length === 1) {
                const resource = resource_finder[0];
                maxed = resource.available >= up_to_max;
              }
            }
          }
          str += " ";
          if (potence.create_resource.resource_type === "Slot") {
            str += `Level ${ potence.create_resource.level } Slot`;
          } else if (potence.create_resource.resource_type === "Charge") {
            str += `Charge`;
          }
          return (
            <Grid className="centered"
              container spacing={0} direction="row">
              <Grid
                className="centered"
                container
                spacing={0}
                direction="row"
                size={{
                  xs: 4
                }}>
                <Grid
                  size={{
                    xs: 12
                  }}>
                  { str }
                </Grid>
                <Grid
                  size={{
                    xs: 12
                  }}>
                  { potence.create_resource.resource_type !== "Slot" && potence.create_resource.resource_type !== "Charge" &&
                    <DisplayObjects type="resource" ids={[potence.create_resource.resource_type]} />
                  }
                </Grid>
              </Grid>
              <Grid
                size={{
                  xs: 4
                }}></Grid>
              <Grid
                size={{
                  xs: 4
                }}>
                <DandDButtonBox 
                  disabled={ !enabled || processing || maxed }
                  name={effect_type}
                  onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                    doRoll("Create");
                    setPopoverMode("Create");
                    setPopoverAnchorEl(event.currentTarget);
                  }}
                />
              </Grid>
              { renderExtras() }
            </Grid>
          );
        } else if (flat) {
          let maxed = false;
          if (up_to_max > 0) {
            str += `up to ${up_to_max} `;
            if (potence.create_resource.resource_type === "Slot") {
              // I'm never going to use it for Slots, or am I?
              debugger;
            } else {
              const resource_finder = character.resources.filter(o => o.type_id === potence.create_resource.resource_type);
              if (resource_finder.length === 1) {
                const resource = resource_finder[0];
                maxed = resource.available >= up_to_max;
              }
            }
          } else if (flat > 1) {
            str += `${flat} `;
          }
          if (potence.create_resource.resource_type === "Slot") {
            str += `Level ${ potence.create_resource.level } Slot`;
          } else if (potence.create_resource.resource_type === "Charge") {
            str += `Charge`;
          }
          return (
            <Grid className="centered"
              container spacing={0} direction="row">
              <Grid
                className="centered"
                container
                spacing={0}
                direction="row"
                size={{
                  xs: 4
                }}>
                <Grid
                  size={{
                    xs: 12
                  }}>
                  { str }
                </Grid>
                <Grid
                  size={{
                    xs: 12
                  }}>
                  { potence.create_resource.resource_type !== "Slot" &&
                    <DisplayObjects type="resource" ids={[potence.create_resource.resource_type]} display_field={flat === 1 ? "singular" : "name"} />
                  }
                </Grid>
              </Grid>
              <Grid
                size={{
                  xs: 4
                }}></Grid>
              <Grid
                size={{
                  xs: 4
                }}>
                <DandDButtonBox 
                  disabled={ !enabled || processing || maxed }
                  name={effect_type}
                  onClick={() => {
                    let item_id = "";
                    if (spell instanceof CharacterAbility) {
                      item_id = spell.item_id;
                    } else {
                      if (ee_cf) {
                        item_id = ee_cf.item_id;
                      }
                      if (item_id === "" && type_cf) {
                        item_id = type_cf.item_id;
                      }
                    }
                    char_util.createResource(character, potence, flat, effect.refresh_only, up_to_max, item_id);
                    setPing(true);
                    onChange(["Resources"]);
                  }}
                />
              </Grid>
              { renderExtras() }
            </Grid>
          );
        }
      }
    } else if (ee_cf) {
      const effect_type = effectType();
      const flat = char_util.value(amount.flat, { obj: character, source_id: ee_cf.source_true_id, root_id: ee_cf.source_true_id, action });
      const count = char_util.value(amount.count, { obj: character, source_id: ee_cf.source_true_id, root_id: ee_cf.source_true_id, action });
      const size = char_util.value(amount.size, { obj: character, source_id: ee_cf.source_true_id, root_id: ee_cf.source_true_id, action });
      const up_to_max = char_util.value(potence.create_resource.up_to_max, { obj: character, source_id: ee_cf.source_true_id, root_id: ee_cf.source_true_id, action });
      if (size !== null && count !== null && flat !== null) {
        let str = effect.refresh_only ? "Refresh " : "Create ";
        if (size > 1 && count > 0) {
          str += `${count}d${size}` + (flat === 0 ? "" : `+${flat}`);
          let maxed = false;
          if (up_to_max > 0) {
            str += ` up to ${up_to_max}`;
            if (potence.create_resource.resource_type === "Slot") {
              // I'm never going to use it for Slots, or am I?
              debugger;
            } else {
              const resource_finder = character.resources.filter(o => o.type_id === potence.create_resource.resource_type);
              if (resource_finder.length === 1) {
                const resource = resource_finder[0];
                maxed = resource.available >= up_to_max;
              }
            }
          }
          str += " ";
          if (potence.create_resource.resource_type === "Slot") {
            str += `Level ${ potence.create_resource.level } Slot`;
          } else if (potence.create_resource.resource_type === "Charge") {
            str += `Charge`;
          }
          return (
            <Grid className="centered"
              container spacing={0} direction="row">
              <Grid
                className="centered"
                container
                spacing={0}
                direction="row"
                size={{
                  xs: 4
                }}>
                <Grid
                  size={{
                    xs: 12
                  }}>
                  { str }
                </Grid>
                <Grid
                  size={{
                    xs: 12
                  }}>
                  { potence.create_resource.resource_type !== "Slot" && potence.create_resource.resource_type !== "Charge" &&
                    <DisplayObjects type="resource" ids={[potence.create_resource.resource_type]} />
                  }
                </Grid>
              </Grid>
              <Grid
                size={{
                  xs: 4
                }}></Grid>
              <Grid
                size={{
                  xs: 4
                }}>
                <DandDButtonBox 
                  disabled={ !enabled || processing || maxed }
                  name={effect_type}
                  onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                    doRoll("Create");
                    setPopoverMode("Create");
                    setPopoverAnchorEl(event.currentTarget);
                  }}
                />
              </Grid>
              { renderExtras() }
            </Grid>
          );
        } else if (flat) {
          let maxed = false;
          if (up_to_max > 0) {
            str += `up to ${up_to_max} `;
            if (potence.create_resource.resource_type === "Slot") {
              // I'm never going to use it for Slots, or am I?
              debugger;
            } else {
              const resource_finder = character.resources.filter(o => o.type_id === potence.create_resource.resource_type);
              if (resource_finder.length === 1) {
                const resource = resource_finder[0];
                maxed = resource.available >= up_to_max;
              }
            }
          } else if (flat > 1) {
            str += `${flat} `;
          }
          if (potence.create_resource.resource_type === "Slot") {
            str += `Level ${ potence.create_resource.level } Slot`;
          } else if (potence.create_resource.resource_type === "Charge") {
            str += `Charge`;
          }
          return (
            <Grid className="centered"
              container spacing={0} direction="row">
              <Grid
                className="centered"
                container
                spacing={0}
                direction="row"
                size={{
                  xs: 4
                }}>
                <Grid
                  size={{
                    xs: 12
                  }}>
                  { str }
                </Grid>
                <Grid
                  size={{
                    xs: 12
                  }}>
                  { potence.create_resource.resource_type !== "Slot" &&
                    <DisplayObjects type="resource" ids={[potence.create_resource.resource_type]} display_field={flat === 1 ? "singular" : "name"} />
                  }
                </Grid>
              </Grid>
              <Grid
                size={{
                  xs: 4
                }}></Grid>
              <Grid
                size={{
                  xs: 4
                }}>
                <DandDButtonBox 
                  disabled={ !enabled || processing || maxed }
                  name={effect_type}
                  onClick={() => {
                    let item_id = "";
                    if (spell instanceof CharacterAbility) {
                      item_id = spell.item_id;
                    } else {
                      if (ee_cf) {
                        item_id = ee_cf.item_id;
                      }
                      if (item_id === "" && type_cf) {
                        item_id = type_cf.item_id;
                      }
                    }
                    char_util.createResource(character, potence, flat, effect.refresh_only, up_to_max, item_id);
                    setPing(true);
                    onChange(["Resources"]);
                  }}
                />
              </Grid>
              { renderExtras() }
            </Grid>
          );
        }
      }
    }
    return null;
  }

  const renderRollableTableControls = () => {
    return (
      <>
        <CharacterRollableTableEffect
          char={character}
          char_interface={char_interface}
          shared={shared}
          effect={effect}
        />
      </>
    );
  }

  const renderSelfConditionControls = () => {
    const potence = ee_cf && (ee_cf.floating_feature instanceof ExtraEffectsFeature || ee_cf.floating_feature instanceof ExtraEffectsOfTypeFeature) ? 
        char_util.getPotence({ slot_level: level, char: character, char_int: char_interface, spell: ee_cf, item_id, effect, cfs_id: (ee_cf ? ee_cf.source_id : ""), attack_mod }) : 
        spell ? char_util.getPotence({ slot_level: level, char: character, char_int: char_interface, spell, effect, item_id, cfs_id: "", attack_mod, action }) : null;
    if (potence) {
      const source_true_id = ee_cf ? ee_cf.source_true_id : (spell ? spell.source_true_id : "");
      return (
        <>
          <CharacterSelfConditionEffect
            character={character}
            effect={effect}
            potence={potence}
            shared={shared}
          />
          { renderExtras() }
        </>
      );
    }
    return <></>;
  }

  const renderSelfGiftControls = () => {
    const potence = ee_cf && (ee_cf.floating_feature instanceof ExtraEffectsFeature || ee_cf.floating_feature instanceof ExtraEffectsOfTypeFeature) ? 
        char_util.getPotence({ slot_level: level, char: character, char_int: char_interface, spell: ee_cf, item_id, effect, cfs_id: (ee_cf ? ee_cf.source_id : ""), attack_mod }) : 
        spell ? char_util.getPotence({ slot_level: level, char: character, char_int: char_interface, spell, effect, item_id, cfs_id: "", attack_mod, action }) : null;
    if (potence) {
      return (
        <>
          <CharacterSelfGiftEffect
            character={character}
            char_interface={char_interface}
            // spell={spell}
            effect={effect}
            potence={potence}
            shared={shared}
            onChange={() => {
              onChange(["All"]);
            }}
          />
          { renderExtras() }
        </>
      );
    }
    return <></>;
  }

  const renderEnhanceItem = () => {
    // Select a valid item
    // Apply the mik
    // Also have a button to clear it from all items.
    // debugger;
    const potence = spell ? char_util.getPotence({ slot_level: level, char: character, char_int: char_interface, item_id, spell, effect, cfs_id: "", attack_mod, action }) : null;
    if (potence) {
      return (
        <CharacterEnhanceItemEffect
          character={character}
          spell={spell}
          processing={processing}
          disabled={disabled}
          shared={shared}
          potence={potence}
          onChange={onChange}
        />
      );
    }
    return <></>;
  }

  const renderEnhanceCreature = () => {
    // Select a valid item
    // Apply the mik
    // Also have a button to clear it from all items.
    // debugger;
    const potence = spell ? char_util.getPotence({ slot_level: level, char: character, char_int: char_interface, item_id, spell, effect, cfs_id: "", attack_mod, action }) : null;
    if (potence) {
      return (
        <CharacterEnhanceCreatureEffect
          character={character}
          spell={spell}
          processing={processing}
          disabled={disabled}
          shared={shared}
          potence={potence}
          onChange={onChange}
        />
      );
    }
    return <></>;
  }

  const renderCreateItem = () => {
    if (spell instanceof CharacterAbility) {
      const potence = spell ? char_util.getPotence({ slot_level: level, char: character, char_int: char_interface, item_id, spell, effect, cfs_id: "", attack_mod, action }) : null;
      if (potence) {
        return (
          <CharacterCreateItemEffect 
            char={character} 
            char_interface={char_interface} 
            spell={spell} 
            action={action} 
            effect={effect} 
            potence={potence}
            disabled={disabled} 
            shared={shared} 
            level={level} 
            casting_time={casting_time}
            onChange={onChange}
          />
        );
      }
      // // if (spell.name.includes("Tinkering")) {
      // //   debugger;
      // // }
      // const potence = spell ? char_util.getPotence({ slot_level: level, char: character, char_int: char_interface, item_id, spell, effect, cfs_id: "", attack_mod, action }) : null;
      // if (potence) {
      //   let pseudo_item_ids: string[] = [];
      //   if (potence.create_item.item_creation_type === "Specific") {
      //     pseudo_item_ids = potence.create_item.pseudo_item_ids;
      //   } else if (potence.create_item.item_creation_type === "Knowledge") {
      //     console.error("Not Implemented");
      //   } else {
      //     if (effect.selectable_key !== "") {
      //       const cfs = character.all_char_feature_sources.find(o => o.true_id === spell.source_true_id);
      //       if (cfs) {
      //         pseudo_item_ids = [cfs.selectable_hash[effect.selectable_key]][0];
      //       }
      //     }
      //   }
      //   if (pseudo_item_ids.length === 1) {
      //     const pseudo_item = char_util.getPseudoItem(pseudo_item_ids[0]);
      //     if (pseudo_item) {
      //       return (
      //         <Grid container spacing={0} direction="row">
      //           <Grid item xs={4}>

      //           </Grid>
      //           <Grid item xs={8} container spacing={0} direction="row">
      //             <Grid item xs={6}>
      //               <ViewItem
      //                 item={pseudo_item}
      //               />
      //             </Grid>
      //             <Grid item xs={3}>
      //               <DandDButtonBox
      //                 name={ `Create` }
      //                 disabled={ !enabled }
      //                 onClick={() => {
      //                   char_util.addItem(character, pseudo_item, 1).then(res => {
      //                     onChange(["Items"]);
      //                   });
      //                 }} 
      //               />
      //             </Grid>
      //             <Grid item xs={3}>
      //               <DandDButtonBox
      //                 name={ `Remove All` }
      //                 disabled={ !enabled }
      //                 onClick={() => {
      //                   // debugger;
      //                   // for (const char_item of valid_items.filter(o => 
      //                   //   o.magic_item_keyword_ids.includes(mik._id))) {
      //                   //   if (char_item) {
      //                   //     char_item.sub_char_feature_sources = 
      //                   //       char_item.sub_char_feature_sources.filter(o => o.source_id !== mik._id);
      //                   //     // char_item.magic_item_keyword_ids = char_item.magic_item_keyword_ids.filter(o => o !== mik._id);
      //                   //   }
      //                   // }
      //                   onChange(["Items"]);
      //                 }} 
      //               />
      //             </Grid>
      //           </Grid>
      //         </Grid>
      //       );
      //     }
      //   } else if (pseudo_item_ids.length > 0) {
      //     const pseudo_items = char_util.getPseudoItems({ pseudo_item_ids });
      //     const options = pseudo_items.map(o => {
      //       const opt: DisablableOption = {
      //         id: o.id,
      //         text: o.name
      //       };
      //       return opt;
      //     });
      //     return (
      //       <Grid container spacing={0} direction="row">
      //         <Grid item xs={4}>

      //         </Grid>
      //         <Grid item xs={8} container spacing={0} direction="row">
      //           <Grid item xs={6}>
      //             <SelectStringBox
      //               name="Select Item"
      //               options={options}
      //               value={selected_id}
      //               onChange={setSelectedId}
      //             />
      //           </Grid>
      //           <Grid item xs={3}>
      //             <DandDButtonBox
      //               name={ `Create` }
      //               disabled={ !enabled || !pseudo_item_ids.includes(selected_id) }
      //               onClick={() => {
      //                 const pseudo_item = pseudo_items.find(o => o.id === selected_id);
      //                 if (pseudo_item) {
      //                   char_util.addItem(character, pseudo_item, 1).then(res => {
      //                     onChange(["Items"]);
      //                   });
      //                 }
      //               }} 
      //             />
      //           </Grid>
      //           <Grid item xs={3}>
      //             <DandDButtonBox
      //               name={ `Remove All` }
      //               disabled={ !enabled }
      //               onClick={() => {
      //                 // debugger;
      //                 // for (const char_item of valid_items.filter(o => 
      //                 //   o.magic_item_keyword_ids.includes(mik._id))) {
      //                 //   if (char_item) {
      //                 //     char_item.sub_char_feature_sources = 
      //                 //       char_item.sub_char_feature_sources.filter(o => o.source_id !== mik._id);
      //                 //     // char_item.magic_item_keyword_ids = char_item.magic_item_keyword_ids.filter(o => o !== mik._id);
      //                 //   }
      //                 // }
      //                 onChange(["Items"]);
      //               }} 
      //             />
      //           </Grid>
      //         </Grid>
      //       </Grid>
      //     );
      //   } else {
      //     return <>Not Implemented</>;
      //   }
      // }
    }
    return <></>;
  }

  const renderSFFeatureSelect = () => {
    // Get the cf of the actual SFFeature and pass it, not this one.
    // debugger;
    const cfb = character.char_feature_bases.find(o => o.feature_base instanceof FeatureBase && o.feature_base.true_id === effect.sf_feature.fb_id);
    if (cfb) {
      const sf_cf = cfb.features.find(o => o.feature_id === effect.sf_feature.f_id);
      if (sf_cf) {
        return (
          <CharacterSpecialFeatureEffect
            char={character}
            shared={shared} 
            obj={sf_cf} 
          />
        );
      }
    }
    return null;
  }

  const renderRoll = () => {
    if (popoverAnchorEl !== null && popoverMode !== "Cast") {
      const mode = popoverMode;
      const ability = spell;
      // debugger;
      if (ability && effect) {
        if (ability instanceof CharacterItem) {
          if (ability instanceof CharacterWeapon) {
            
          } else {

          }
        } else if (ability instanceof Attack) {
          
        } else {
          const effect_type = effectType();
          if (ability.the_spell) {
            if (mode === "Attack") {
              return (
                <Roller 
                  name={ability.name}
                  rolled_values={roll} 
                  type="Attack" 
                />
              );
            } else if (mode === "Damage") {
              if (effect_type === "Healing" || effect_type === "Temp HP" || effect_type === "Max HP") {
                return (
                  <Roller 
                    name={ability.name}
                    char={character}
                    rolled_values={roll}
                    type={effect_type} 
                  />
                );
              } else {
                return (
                  <Roller 
                    name={ability.name}
                    rolled_values={roll}
                    type="Damage" 
                  />
                );
              }
            } else if (mode === "Create") {
              console.log("Not Implemented");
            }
          } else if (ability instanceof CharacterSpell || ability instanceof StoredSpell) {

          } else if (ability.the_ability instanceof Ability || 
            ability.the_ability instanceof CreatureAbility) {
            if (mode === "Attack") {
              return (
                <Roller 
                  name={ability.name}
                  rolled_values={roll}
                  type="Attack" 
                />
              );
            } else if (mode === "Damage") {
              if (effect_type === "Healing" || effect_type === "Temp HP" || effect_type === "Max HP") {
                return (
                  <Roller 
                    name={ability.name}
                    char={character}
                    rolled_values={roll}
                    type={effect_type} 
                  />
                );
              } else {
                return (
                  <Roller 
                    name={ability.name}
                    rolled_values={roll}
                    type="Damage" 
                  />
                );
              }
            } else if (mode === "Create") {
              const potence = char_util.getPotence({ slot_level: level, char: character, char_int: char_interface, item_id, spell: (ee_cf && (ee_cf.floating_feature instanceof ExtraEffectsFeature || ee_cf.floating_feature instanceof ExtraEffectsOfTypeFeature) ? ee_cf : ability), effect, cfs_id: (ee_cf ? ee_cf.source_id : ""), attack_mod, action });
              if (potence) {
                return (
                  <Roller 
                    name={ability.name}
                    rolled_values={roll}
                    type={effect_type} 
                  />
                );
              }
              return null;
            } else {
              console.error("Not Implemented");
              debugger;
            }
          }
        }
      } else if (effect && ee_cf && 
        (ee_cf.floating_feature instanceof ExtraEffectsFeature || ee_cf.floating_feature instanceof ExtraEffectsOfTypeFeature)) {
        const ff = ee_cf.floating_feature;
        const effect_type = effectType();
        if (mode === "Attack") {
        } else if (mode === "Damage") {
          if (effect_type === "Healing" || effect_type === "Temp HP" || effect_type === "Max HP") {
            return (
              <Roller 
                name={ff.name}
                rolled_values={roll}
                type={effect_type} 
              />
            );
          } else {
            return (
              <Roller 
                name={ff.name}
                rolled_values={roll}
                type="Damage" 
              />
            );
          }
        } else if (mode === "Create") {
          const potence = char_util.getPotence({ slot_level: level, char: character, char_int: char_interface, item_id, spell: ee_cf, effect, cfs_id: (ee_cf ? ee_cf.source_id : ""), attack_mod, action });
          if (potence) {
            return (
              <Roller 
                name={ff.name}
                rolled_values={roll}
                type={effect_type} 
              />
            );
          }
          return null;
        } else {
          console.error("Not Implemented");
          debugger;
        }
      }
    }
    return null;
  }

  if (log_it) {
    console.log(effect);
    // debugger;
  }
  // if (effect.type === "Use Extra") {
  //   debugger;
  // }
  // if (spell && spell.name.includes("Starry Wisp")) {
  //   debugger;
  // }
  // if (ee_cf && ee_cf.name.includes("Vex")) {
  //   console.log(ee_cf);
  //   debugger;
  // }
  // if (action.name.includes("Tricks")) {
  //   debugger;
  // }
  const effect_type = effectType();
  if (effect_type === "Rollable Table") {
    return renderRollableTableControls();
  } else if (effect_type === "Self Condition") {
    return renderSelfConditionControls();
  } else if (effect_type === "Self Gift") {
    return renderSelfGiftControls();
  } else if (effect_type === "Enhance Item") {
    return renderEnhanceItem();
  } else if (effect_type === "Enhance Creature") {
    return renderEnhanceCreature();
  } else if (effect_type === "Create Item") {
    return renderCreateItem();
  } else if (effect_type === "Special Feature") {
    return renderSFFeatureSelect();
  } else if (effect_type === "Draw Card") {
    if (spell instanceof CharacterAbility) {
      return (
        <CharacterCardEffect 
          character={character} 
          spell={spell} 
          effect={effect} 
          processing={false} 
          disabled={false} 
          shared={false}  
          onChange={() => {
            // debugger;
            onChange(["All"]);
          }}        
        />
      );
    }
  } else if (effect_type === "Use Extra") {
    debugger;
  } else {
    // const potence = effect.potences.length > 0 ? effect.potences[0] : null;
    if (log_it) {
      console.log(spell);
    }
    // if (ee_cf && ee_cf.floating_feature) {
    //   debugger;
    // }
    let potence = ee_cf && (ee_cf.floating_feature instanceof ExtraEffectsFeature || ee_cf.floating_feature instanceof ExtraEffectsOfTypeFeature) ? 
      char_util.getPotence({ slot_level: level, char: character, char_int: char_interface, spell: ee_cf, item_id, effect, cfs_id: (ee_cf ? ee_cf.source_id : ""), attack_mod, action, log_it }) : 
      spell ? char_util.getPotence({ slot_level: level, char: character, char_int: char_interface, spell, effect, item_id, cfs_id: "", attack_mod, action, log_it }) : undefined;
    let extra_string = "";
    if (potence) {
      extra_string = potence.extra;
      if (["Summon"].includes(effect.type)) {
        return (
          <CharacterSummonEffect 
            character={character}
            spell={spell}
            effect={effect}
            processing={processing}
            disabled={disabled}
            shared={shared}
            char_interface={char_interface}
            level={level}
            wide={wide}
            casting_time={casting_time}
            onCostChange={onCostChange} 
            ee_cf={ee_cf}
            action={action} 
            onChange={onChange} 
            extra_string={extra_string}
          />
        );
      } else if (effect.type === "Transform") {
        return (
          <CharacterTransformEffect 
            character={character}
            spell={spell}
            effect={effect}
            processing={processing}
            disabled={disabled}
            shared={shared} 
            char_interface={char_interface} 
            level={level} 
            wide={wide} 
            casting_time={casting_time} 
            // onChange={onChange} 
            onCostChange={onCostChange} 
            // onTransform={onTransform}
          />
        );
      // } else if (effect.type === "Adaptation") {
      //   return renderAdaptationControls(potence, extra_string);
      } else if (effect_type === "Create Resource") {
        return renderCreateResourceControls(potence);
      }
    }
    if (potence || effect_type === "Utility" || effect_type === "Control") {
      return (
        <CharacterEffectStandard 
          character={character}
          char_interface={char_interface}
          spell={spell}
          action={action}
          effect={effect}
          cee={cee}
          processing={processing}
          disabled={disabled}
          log_it={log_it}
          ee_cf={ee_cf}
          type_cf={type_cf}
          shared={shared}
          level={level}
          attack_mod={attack_mod}
          main_damage_type={main_damage_type}
          hide_touch={hide_touch}
          potence={potence}
          effect_type={effect_type}   
        />
      );
    }
  }
  return <></>;
}
